import {
  Box,
  Button,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import { History, RemoveRedEye, RotateLeft } from '@material-ui/icons';
import SearchIcon from '@material-ui/icons/Search';
import { Alert, Autocomplete } from '@material-ui/lab';
import classNames from 'classnames';
import { DatePicker, PageableTable, SnackbarContext } from 'components';
import { DateDisplay } from 'components/DateDisplay/DateDisplay';
import { PageableTableHeader } from 'components/PageableTable/components';
import { RelatedCompanyChip } from 'components/RelatedCompanyChip';
import { DateFormat } from 'config';
import { endOfDay, format, parseISO, startOfDay, startOfYear } from 'date-fns';
import {
  TableSettings,
  getTableSettingsStorageKey,
  useDebounce,
  useLocalStorageSettings,
} from 'hooks';
import { toMonetary } from 'lib';
import {
  exportDeals,
  fetchAllDeals,
  fetchDealAudit,
  fetchDealDetails,
  fetchDealFilterOptions,
} from 'lib/Api/Deals';
import { Deal, DealAudit, DealFilterOptions, PagedDeal } from 'lib/Model/Deal';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import DealAuditDialog from 'views/Clients/components/DealAuditDialog';
import DealDetailsDialog from 'views/Clients/components/DealDetailsDialog';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2),
  },
  clickable: {
    cursor: 'pointer',
    color: theme.palette.secondary.main,
    textAlign: 'left',
  },
  boldTitle: {
    fontWeight: 500,
    padding: 0,
    clear: 'both',
    display: 'block',
    cursor: 'default',
  },
  // A fixed layout with percentages that total 100%, so the table always
  // matches the page width instead of scrolling sideways.
  table: {
    tableLayout: 'fixed',
    '& td, & th': {
      overflow: 'hidden',
    },
  },
  dateColumn: {
    width: '8%',
  },
  titleColumn: {
    width: '13%',
  },
  companyColumn: {
    width: '13%',
    // the chip label never wraps, so cap it and let it ellipsis
    '& .MuiChip-root': {
      maxWidth: '100%',
    },
  },
  serviceColumn: {
    width: '8%',
  },
  productColumn: {
    width: '11%',
  },
  pipelineColumn: {
    width: '9%',
  },
  stageColumn: {
    width: '13%',
  },
  ownerColumn: {
    width: '8%',
  },
  valueColumn: {
    width: '7%',
  },
  iconColumn: {
    width: '5%',
  },
  textControl: {
    marginBottom: theme.spacing(2),
  },
  formControl: {
    marginBottom: theme.spacing(0.5),
  },
  filterRow: {
    marginBottom: theme.spacing(1),
  },
  filterChip: {
    margin: 2,
    maxWidth: 150,
  },
}));

export const DealsView: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const snackbar = React.useContext(SnackbarContext);
  const [loading, setLoading] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [pageNumber, setPageNumber] = React.useState(0);

  const { pathname, hash } = useLocation();
  const [tableSettings, setTableSettings] =
    useLocalStorageSettings<TableSettings>(
      getTableSettingsStorageKey(pathname, hash),
      { rowsPerPage: 25 },
    );

  const [data, setData] = React.useState<PagedDeal | undefined>();
  const [textSearch, setTextSearch] = React.useState<string | undefined>();
  const debouncedTextSearch = useDebounce(textSearch, 400);
  const [selectedServices, setSelectedServices] = React.useState<string[]>([]);
  const [selectedProducts, setSelectedProducts] = React.useState<string[]>([]);
  const [selectedPipelines, setSelectedPipelines] = React.useState<string[]>(
    [],
  );
  const [selectedStages, setSelectedStages] = React.useState<string[]>([]);
  const [selectedOwners, setSelectedOwners] = React.useState<string[]>([]);
  // Default to year to date — a month of deals is often close to empty.
  const today = React.useMemo(() => startOfDay(new Date()), []);
  const defaultDateFrom = React.useMemo(
    () => startOfYear(today).toISOString(),
    [today],
  );
  const defaultDateTo = React.useMemo(
    () => endOfDay(today).toISOString(),
    [today],
  );

  const [dateFrom, setDateFrom] = React.useState<string | undefined>(
    defaultDateFrom,
  );
  const [dateTo, setDateTo] = React.useState<string | undefined>(defaultDateTo);
  const [hideClosedDeals, setHideClosedDeals] = React.useState(false);
  const [filterOptions, setFilterOptions] = React.useState<
    DealFilterOptions | undefined
  >();

  const [viewMoreDialogOpen, setViewMoreDialogOpen] = React.useState(false);
  const [dialogDataLoading, setDialogDataLoading] = React.useState(false);
  const [selectedDeal, setSelectedDeal] = React.useState<Deal[]>();
  const [dealHistoryDialogOpen, setDealHistoryDialogOpen] =
    React.useState(false);
  const [selectedDealHistory, setSelectedDealHistory] = React.useState<
    DealAudit[]
  >([]);

  React.useEffect(() => {
    fetchDealFilterOptions()
      .then(setFilterOptions)
      .catch(() => {
        setFilterOptions({
          services: [],
          pipelines: [],
          stages: [],
          stages_by_pipeline: {},
          owners: [],
          products: [],
        });
      });
  }, []);

  const servicesParam = selectedServices.join(',') || undefined;
  const productsParam = selectedProducts.join(',') || undefined;
  const pipelinesParam = selectedPipelines.join(',') || undefined;
  const stagesParam = selectedStages.join(',') || undefined;

  // Stage names belong to a pipeline, so narrow the stage list once one is picked.
  const stageOptions = selectedPipelines.length
    ? selectedPipelines.flatMap(
        (pipeline) => filterOptions?.stages_by_pipeline?.[pipeline] ?? [],
      )
    : filterOptions?.stages ?? [];

  // Drop any stage that the newly picked pipelines do not have.
  React.useEffect(() => {
    setSelectedStages((stages) =>
      stages.filter((stage) => stageOptions.includes(stage)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelinesParam]);
  const ownersParam = selectedOwners.join(',') || undefined;

  const hasActiveFilters =
    Boolean(textSearch) ||
    selectedServices.length > 0 ||
    selectedProducts.length > 0 ||
    selectedPipelines.length > 0 ||
    selectedStages.length > 0 ||
    selectedOwners.length > 0 ||
    dateFrom !== defaultDateFrom ||
    dateTo !== defaultDateTo ||
    hideClosedDeals;

  const resetFilters = () => {
    setTextSearch(undefined);
    setSelectedServices([]);
    setSelectedProducts([]);
    setSelectedPipelines([]);
    setSelectedStages([]);
    setSelectedOwners([]);
    setDateFrom(defaultDateFrom);
    setDateTo(defaultDateTo);
    setHideClosedDeals(false);
    setPageNumber(0);
  };

  const fetchData = React.useCallback(() => {
    setLoading(true);
    setError(false);
    fetchAllDeals(
      pageNumber + 1,
      tableSettings.rowsPerPage,
      hideClosedDeals,
      debouncedTextSearch,
      servicesParam,
      pipelinesParam,
      stagesParam,
      ownersParam,
      productsParam,
      dateFrom,
      dateTo,
    )
      .then((d) => {
        setData(d);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    pageNumber,
    tableSettings.rowsPerPage,
    hideClosedDeals,
    debouncedTextSearch,
    servicesParam,
    pipelinesParam,
    stagesParam,
    ownersParam,
    productsParam,
    dateFrom,
    dateTo,
  ]);

  const exportData = React.useCallback(async () => {
    setExporting(true);
    snackbar.info('Preparing your export…');
    try {
      await exportDeals(
        hideClosedDeals,
        debouncedTextSearch,
        servicesParam,
        pipelinesParam,
        stagesParam,
        ownersParam,
        productsParam,
        dateFrom,
        dateTo,
      );
      snackbar.success('Your export has downloaded.');
    } catch {
      snackbar.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [
    snackbar,
    hideClosedDeals,
    debouncedTextSearch,
    servicesParam,
    pipelinesParam,
    stagesParam,
    ownersParam,
    productsParam,
    dateFrom,
    dateTo,
  ]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to first page when filters change
  React.useEffect(() => {
    setPageNumber(0);
  }, [
    debouncedTextSearch,
    servicesParam,
    pipelinesParam,
    stagesParam,
    ownersParam,
    productsParam,
    dateFrom,
    dateTo,
    hideClosedDeals,
  ]);

  const handleOpenViewMoreDialog = async (dealId: string) => {
    setViewMoreDialogOpen(true);
    setDialogDataLoading(true);
    const dealDetails = await fetchDealDetails(dealId);
    setSelectedDeal(dealDetails);
    setDialogDataLoading(false);
  };

  const handleOpenDealHistoryDialog = async (dealId: string) => {
    setDialogDataLoading(true);
    setDealHistoryDialogOpen(true);

    try {
      const auditData = await fetchDealAudit(dealId);
      setSelectedDealHistory(auditData);
    } finally {
      setDialogDataLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setViewMoreDialogOpen(false);
    setDealHistoryDialogOpen(false);
  };

  const columns: PageableTableHeader[] = [
    {
      key: 'dateCreated',
      label: 'Date created',
      sortable: false,
      props: {
        className: classes.dateColumn,
      },
    },
    {
      key: 'title',
      label: 'Deal name',
      sortable: false,
      props: {
        className: classes.titleColumn,
      },
    },
    {
      key: 'company',
      label: 'Company',
      sortable: false,
      props: {
        className: classes.companyColumn,
      },
    },
    {
      key: 'service',
      label: 'Service',
      sortable: false,
      props: {
        className: classes.serviceColumn,
      },
    },
    {
      key: 'product',
      label: 'Product(s)',
      sortable: false,
      props: {
        className: classes.productColumn,
      },
    },
    {
      key: 'pipeline',
      label: 'Pipeline',
      sortable: false,
      props: {
        className: classes.pipelineColumn,
      },
    },
    {
      key: 'stage',
      label: 'Stage',
      sortable: false,
      props: {
        className: classes.stageColumn,
      },
    },
    {
      key: 'owner',
      label: 'Owner',
      sortable: false,
      props: {
        className: classes.ownerColumn,
      },
    },
    {
      key: 'value',
      label: 'Deal Value',
      sortable: false,
      props: {
        className: classes.valueColumn,
      },
    },
    {
      key: 'viewMore',
      label: '',
      sortable: false,
      props: {
        className: classes.iconColumn,
      },
    },
    {
      key: 'history',
      label: '',
      sortable: false,
      props: {
        className: classes.iconColumn,
      },
    },
  ];

  return (
    <Card className={classes.card}>
      <CardHeader
        title="Deals"
        subheader="Proposals tracked by our business development team, across every client."
        action={
          <Button color="secondary" onClick={exportData} disabled={exporting}>
            Export Data
            {exporting && (
              <Box ml={1} display="flex">
                <CircularProgress size={16} />
              </Box>
            )}
          </Button>
        }
      />
      <CardContent style={{ paddingTop: 0, paddingBottom: 8 }}>
        <TextField
          className={classes.textControl}
          value={textSearch ?? ''}
          variant="outlined"
          autoFocus
          onChange={(e) => setTextSearch(e.currentTarget.value || undefined)}
          fullWidth
          placeholder="Search by deal name, company, or owner"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Grid container spacing={2} className={classes.filterRow}>
          <Grid item xs={12} md={3}>
            <FormControl
              className={classes.formControl}
              variant="outlined"
              fullWidth
            >
              <Autocomplete
                multiple
                options={filterOptions?.services ?? []}
                renderInput={(params) => (
                  <TextField
                    variant="outlined"
                    label="Service(s)"
                    {...params}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    placeholder={
                      selectedServices.length === 0 ? 'All services' : ''
                    }
                  />
                )}
                value={selectedServices}
                onChange={(_e, newValue) => setSelectedServices(newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      size="small"
                      label={option}
                      className={classes.filterChip}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                limitTags={2}
                disableCloseOnSelect
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl
              className={classes.formControl}
              variant="outlined"
              fullWidth
            >
              <Autocomplete
                multiple
                options={filterOptions?.products ?? []}
                renderInput={(params) => (
                  <TextField
                    variant="outlined"
                    label="Product(s)"
                    {...params}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    placeholder={
                      selectedProducts.length === 0 ? 'All products' : ''
                    }
                  />
                )}
                value={selectedProducts}
                onChange={(_e, newValue) => setSelectedProducts(newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      size="small"
                      label={option}
                      className={classes.filterChip}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                limitTags={2}
                disableCloseOnSelect
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl
              className={classes.formControl}
              variant="outlined"
              fullWidth
            >
              <Autocomplete
                multiple
                options={filterOptions?.pipelines ?? []}
                renderInput={(params) => (
                  <TextField
                    variant="outlined"
                    label="Pipeline(s)"
                    {...params}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    placeholder={
                      selectedPipelines.length === 0 ? 'All pipelines' : ''
                    }
                  />
                )}
                value={selectedPipelines}
                onChange={(_e, newValue) => setSelectedPipelines(newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      size="small"
                      label={option}
                      className={classes.filterChip}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                limitTags={2}
                disableCloseOnSelect
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl
              className={classes.formControl}
              variant="outlined"
              fullWidth
            >
              <Autocomplete
                multiple
                options={stageOptions}
                renderInput={(params) => (
                  <TextField
                    variant="outlined"
                    label="Stage(s)"
                    {...params}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    placeholder={
                      selectedStages.length === 0 ? 'All stages' : ''
                    }
                  />
                )}
                value={selectedStages}
                onChange={(_e, newValue) => setSelectedStages(newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      size="small"
                      label={option}
                      className={classes.filterChip}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                limitTags={2}
                disableCloseOnSelect
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl
              className={classes.formControl}
              variant="outlined"
              fullWidth
            >
              <Autocomplete
                multiple
                options={filterOptions?.owners ?? []}
                renderInput={(params) => (
                  <TextField
                    variant="outlined"
                    label="Owner(s)"
                    {...params}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    placeholder={
                      selectedOwners.length === 0 ? 'All owners' : ''
                    }
                  />
                )}
                value={selectedOwners}
                onChange={(_e, newValue) => setSelectedOwners(newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      size="small"
                      label={option}
                      className={classes.filterChip}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                limitTags={2}
                disableCloseOnSelect
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl className={classes.formControl} fullWidth>
              <DatePicker
                label="Created from"
                margin="none"
                fullWidth
                value={dateFrom}
                inputVariant="outlined"
                InputLabelProps={{ shrink: true }}
                onChange={(date: MaterialUiPickersDate) => {
                  setDateFrom(date ? date.toISOString() : undefined);
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl className={classes.formControl} fullWidth>
              <DatePicker
                label="Created to"
                margin="none"
                fullWidth
                value={dateTo}
                inputVariant="outlined"
                InputLabelProps={{ shrink: true }}
                onChange={(date: MaterialUiPickersDate) => {
                  setDateTo(date ? date.toISOString() : undefined);
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={hideClosedDeals}
                  onChange={(e, checked) => setHideClosedDeals(checked)}
                />
              }
              label="Hide closed deals"
            />
          </Grid>
        </Grid>

        {hasActiveFilters && (
          <Button
            size="small"
            startIcon={<RotateLeft />}
            onClick={resetFilters}
            style={{ marginBottom: 4 }}
          >
            Reset Filters
          </Button>
        )}
      </CardContent>

      {error ? (
        <CardContent>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={fetchData}>
                Retry
              </Button>
            }
          >
            Could not load deals. Please try again.
          </Alert>
        </CardContent>
      ) : (
        <PageableTable
          tableProps={{ className: classes.table }}
          tableSettings={tableSettings}
          setTableSettings={setTableSettings}
          showSkeletonLoading
          rows={
            data?.data?.map((d, index) => ({
              key: `deal_${index}`,
              cells: [
                {
                  key: 'dateCreated',
                  display: <DateDisplay date={d.created_at} />,
                },
                {
                  key: 'title',
                  display: (
                    <React.Fragment>
                      <div>
                        <Button
                          className={classNames(
                            d.deal_url
                              ? [classes.boldTitle, classes.clickable]
                              : [classes.boldTitle],
                          )}
                          variant="text"
                          onClick={
                            d.deal_url
                              ? () => {
                                  window.open(
                                    d.deal_url,
                                    '_blank',
                                    'noopener,noreferrer',
                                  );
                                }
                              : undefined
                          }
                        >
                          {d.deal_name}
                        </Button>
                      </div>
                      <div>
                        <Typography variant="caption">
                          Deal ID: {d.id}
                        </Typography>
                      </div>
                    </React.Fragment>
                  ),
                },
                {
                  key: 'company',
                  display: (
                    <RelatedCompanyChip
                      companyId={d.company_id}
                      companyName={d.company_title}
                    />
                  ),
                },
                {
                  key: 'service',
                  display: d.deal_service,
                },
                {
                  key: 'product',
                  display: (
                    <React.Fragment>
                      {d.vacancies?.length
                        ? d.vacancies.map((v) => (
                            <Chip
                              style={{ margin: 2 }}
                              label={v}
                              size="small"
                            />
                          ))
                        : d.deal_products?.map((product) => (
                            <Chip
                              style={{ margin: 2 }}
                              label={product}
                              size="small"
                            />
                          ))}
                    </React.Fragment>
                  ),
                },
                {
                  key: 'pipeline',
                  display: d.pipeline,
                },
                {
                  key: 'stage',
                  display: (
                    <React.Fragment>
                      <div>
                        <Typography
                          variant="body1"
                          style={{
                            color:
                              d.stage === 'Won'
                                ? theme.palette.success.main
                                : ['Lost', 'Unqualified'].includes(d.stage)
                                ? theme.palette.error.main
                                : theme.palette.info.main,
                            textTransform: 'uppercase',
                            fontWeight: 500,
                          }}
                        >
                          {d.stage.toUpperCase()}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="caption">
                          {d.loss_reason}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="caption">
                          {d.last_stage_change_date &&
                            `Updated on: ${format(
                              parseISO(d.last_stage_change_date),
                              DateFormat.SHORT,
                            )}`}
                        </Typography>
                      </div>
                    </React.Fragment>
                  ),
                },
                {
                  key: 'owner',
                  display: d.owner,
                },
                {
                  key: 'value',
                  display: toMonetary(d.deal_value ?? 0),
                },
                {
                  key: 'viewMore',
                  display: (
                    <IconButton
                      size="small"
                      onClick={() => handleOpenViewMoreDialog(d.id)}
                      color="primary"
                    >
                      <RemoveRedEye />
                    </IconButton>
                  ),
                },
                {
                  key: 'history',
                  display: (
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDealHistoryDialog(d.id)}
                      color="primary"
                    >
                      <History />
                    </IconButton>
                  ),
                },
              ],
            })) ?? []
          }
          loading={loading}
          pageNumber={pageNumber}
          rowCount={data?.total_count ?? 0}
          columns={columns}
          emptyTableText="No deals match these filters. Try clearing a filter."
          onChangePage={setPageNumber}
        />
      )}

      <DealDetailsDialog
        open={viewMoreDialogOpen}
        deal={selectedDeal!}
        loading={dialogDataLoading}
        onClose={handleCloseDialog}
      />
      <DealAuditDialog
        open={dealHistoryDialogOpen}
        dealAudit={selectedDealHistory}
        loading={dialogDataLoading}
        onClose={handleCloseDialog}
      />
    </Card>
  );
};
